import { Button, ModalDialog, ModalOverlay, NumberField } from '@flos/react-ui';
import { useState } from 'react';
import { useHippoText } from '../../../../hooks/useHippoText';
import { useRecoilValue } from 'recoil';
import { yearlyMileageOptionsSelector } from '../../../../recoil/selectors/yearlyMileageOptionsSelector';
import type { MVPYearlyMileage_Top } from 'gw-edge-client/dist/types/dto/pc/typekey';

const CalculatorModal = ({
    changeMileage,
    setYearlyMileage,
    onCalculatorMileageChange,
    showModal,
    closeModal
}: {
    changeMileage?: (mileageCode: string) => void;
    setYearlyMileage?: React.Dispatch<React.SetStateAction<string | undefined>>;
    onCalculatorMileageChange?: (value: MVPYearlyMileage_Top) => void;
    showModal: boolean;
    closeModal: () => void;
}): React.JSX.Element => {
    const getHippoText = useHippoText();
    const mileageOptions = useRecoilValue(yearlyMileageOptionsSelector);
    const [commuteKm, setCommuteKm] = useState(0);
    const [vacationKm, setVacationKm] = useState(0);
    const [smallTripsKm, setSmallTripsKm] = useState(0);
    const [result, setResult] = useState(0);

    const handleResult = (result: number): void => {
        let matchedInterval;
        if (result > 40000) {
            matchedInterval = 'Unlimited';
        } else if (
            mileageOptions.find((option) => option.code === '0To3KVintageCar') &&
            result < 3000
        ) {
            matchedInterval = '0To3KVintageCar';
        } else {
            const interval = mileageOptions.find((option) => {
                if (option.code === '0To3KVintageCar') {
                    return false;
                }
                const [lowerStr, upperStr] = option.code
                    .split('To')
                    .map((str) => str.match(/[0-9]+/));
                if (lowerStr?.length && upperStr?.length) {
                    const lower = Number(lowerStr[0]) * 1000;
                    const upper = Number(upperStr[0]) * 1000;
                    return result > lower && result < upper;
                }
                return false;
            });
            matchedInterval = interval?.code;
        }
        if (matchedInterval) {
            changeMileage?.(matchedInterval);
            setYearlyMileage?.(matchedInterval);
            onCalculatorMileageChange?.(matchedInterval as MVPYearlyMileage_Top);
        }
    };

    const updateResult = (commute: number, vacation: number, smallTrip: number): void => {
        const newResult = commute * 220 + vacation + smallTrip * 365;
        setResult(newResult);
    };

    return (
        <ModalOverlay isOpen={showModal} onDismiss={closeModal}>
            <ModalDialog closeIconHandler={closeModal}>
                <div className="u-spacing-inset-l">
                    <div className="u-spacing-inset-l--stretch-max">
                        <h1 className="u-spacing-stack-sm">
                            {getHippoText('DSS-CALCULATOR-MODAL-HEADER')}
                        </h1>
                        <p className="u-spacing-stack-sm">
                            {getHippoText('DSS-CALCULATOR-MODAL-SUBHEADER')}
                        </p>
                    </div>
                    <h6>{getHippoText('DSS-CALCULATOR-MODAL-DRIVE-DAILY-WORK-TITLE')}</h6>
                    <NumberField
                        placeholder=""
                        label={getHippoText('DSS-CALCULATOR-MODAL-FIELD')}
                        withFormatting={true}
                        suffix=" km"
                        value={commuteKm.toString()}
                        onValueChange={(values) => {
                            setCommuteKm(Number(values.value));
                            updateResult(Number(values.value), vacationKm, smallTripsKm);
                        }}
                    />
                    <h6>{getHippoText('DSS-CALCULATOR-MODAL-YEARLY-ROAD-TRIPS-TITLE')}</h6>
                    <NumberField
                        placeholder=""
                        label={getHippoText('DSS-CALCULATOR-MODAL-FIELD')}
                        withFormatting={true}
                        suffix=" km"
                        value={vacationKm.toString()}
                        onValueChange={(values) => {
                            setVacationKm(Number(values.value));
                            updateResult(commuteKm, Number(values.value), smallTripsKm);
                        }}
                    />
                    <h6>{getHippoText('DSS-CALCULATOR-MODAL-DAILY-SMALL-TRIPS-TITLE')}</h6>
                    <NumberField
                        placeholder=""
                        label={getHippoText('DSS-CALCULATOR-MODAL-FIELD')}
                        withFormatting={true}
                        suffix=" km"
                        value={smallTripsKm.toString()}
                        onValueChange={(values) => {
                            setSmallTripsKm(Number(values.value));
                            updateResult(commuteKm, vacationKm, Number(values.value));
                        }}
                    />
                    <div className="u-spacing-inset-l--stretch-max">
                        <p className="u-spacing-stack-sm">
                            {getHippoText('DSS-CALCULATOR-MODAL-YEARLY-MILEAGE-HEADER')}
                        </p>
                        <h1 className="u-spacing-stack-sm">{result} km</h1>
                    </div>
                    <Button
                        onClick={() => {
                            handleResult(result);
                            closeModal();
                        }}
                    >
                        {getHippoText('DSS-CALCULATOR-MODAL-BUTTON')}
                    </Button>
                </div>
            </ModalDialog>
        </ModalOverlay>
    );
};
export default CalculatorModal;
